import React, { useReducer, useEffect, useMemo } from "react"
import { at, flattenDeep, get, map, find, isEmpty } from "lodash"
import {
  RankingDetailTemplate,
  RankingSummaryTemplate,
} from "components/templates/domestic"
import {
  getRankingType,
  rankingList,
  rankingReducer,
  rankingState,
  requestToRankingData,
  initialTypeNumsOfForeign,
} from "components/actions/ranking"
import { checkIsSp } from "utils/util"
import { Sort } from "utils/sort"
import { Config } from "config/config"
import { UserStorage } from "utils/userStorage"
import { withRouter, WithRouterProps } from "react-router"
import uuid from "uuid"
import store from "redux/store"
import { pageLoadRegist } from "redux/ducks/PageLoad"
import { LoadStateType } from "redux/ducks/PageLoad"
import { useSelector } from "react-redux"

//外国株:ランキング
export const ForeignRanking: WithRouterProps<React.FC> = withRouter(
  ({ location }) => {
    const [foreignRankingData, changeForeignRanking] = useReducer(
      rankingReducer,
      rankingState
    )
    const isPhone = useMemo(() => checkIsSp(), [])
    const loadResistId = useMemo(() => uuid(), [])
    const isPageLoading = useSelector(
      (state: { PageLoad: LoadStateType }) => state.PageLoad.load
    )

    const rankingSetting = isEmpty(UserStorage.ForeignRanking.get())
      ? initialTypeNumsOfForeign
      : UserStorage.ForeignRanking.get()
    const rankingTypes = useMemo(
      () =>
        map(rankingSetting, (num: number) => {
          return getRankingType(num, 0, true)
        }),
      []
    )
    const rankingTypeNums = map(rankingSetting, (num: number) => {
      return get(
        find(Sort.RankingSortByForeign, data => {
          return data.rankingTypeNum === num
        }),
        "id",
        NaN
      )
    })

    //DidMount
    useEffect(() => {
      ;(async () => {
        store.dispatch(pageLoadRegist(loadResistId, true))
        if (isEmpty(UserStorage.ForeignRanking.get())) {
          UserStorage.ForeignRanking.set(initialTypeNumsOfForeign)
        }
        if (isPhone) {
          await requestToRankingData({
            actionType: "SMARTPHONE",
            exchange: 0,
            rankingType: 100303,
            rankingTypeNum: [0],
            take: [50],
            changeRanking: changeForeignRanking,
            loadResistId: loadResistId,
          })
        } else if (get(location.state, "isDetails")) {
          await requestToRankingData({
            actionType: "SUMMARY_EXCHANGE_TRANSITION",
            exchange: 0,
            rankingType: [...rankingTypes],
            rankingTypeNum: rankingTypeNums,
            take: [10, 10, 10, 10],
            changeRanking: changeForeignRanking,
            loadResistId: loadResistId,
          })
          await requestToRankingData({
            actionType: "DETAILS_EXCHANGE",
            exchange: get(location.state, "exchange"),
            rankingType: get(location.state, "type"),
            rankingTypeNum: [get(location.state, "typeNum")],
            take: [50],
            changeRanking: changeForeignRanking,
            loadResistId: loadResistId,
          })
        } else {
          await requestToRankingData({
            actionType: "INITIAL",
            exchange: 0,
            rankingType: [...rankingTypes, 140303],
            rankingTypeNum: rankingTypeNums,
            take: [10, 10, 10, 10, 50],
            changeRanking: changeForeignRanking,
            loadResistId: loadResistId,
          })
        }
        store.dispatch(pageLoadRegist(loadResistId, false))
      })()
    }, [])

    const summaryRankingType =
      Sort.ForeignRankingExchangeFull[foreignRankingData.summaryExchange]
        .rankingType
    const summaryTypeList = flattenDeep(at(rankingList, summaryRankingType))
    const summaryRankingTypes = [
      get(foreignRankingData, "summaryType1.num"),
      get(foreignRankingData, "summaryType2.num"),
      get(foreignRankingData, "summaryType3.num"),
      get(foreignRankingData, "summaryType4.num"),
    ]

    const dataUpdate = () => {
      store.dispatch(pageLoadRegist(loadResistId, true))
      //サマリ表示
      if (!foreignRankingData.displayType) {
        const _rankingTypes = map(summaryRankingTypes, (type: number) => {
          return getRankingType(
            get(summaryTypeList[type], "rankingTypeNum"),
            foreignRankingData.summaryExchange,
            true
          )
        })
        requestToRankingData({
          actionType: "SUMMARY_EXCHANGE",
          exchange: foreignRankingData.summaryExchange,
          rankingType: _rankingTypes,
          rankingTypeNum: summaryRankingTypes,
          take: [10, 10, 10, 10],
          changeRanking: changeForeignRanking,
          loadResistId: loadResistId,
        })
        store.dispatch(pageLoadRegist(loadResistId, false))
      }
      //詳細表示
      else {
        const sortStatus = get(foreignRankingData, "sortStatus")
        const detailsTypeNum = get(foreignRankingData, "detailsType.num")
        const _rankingTypes = getRankingType(
          get(summaryTypeList[detailsTypeNum], "rankingTypeNum"),
          foreignRankingData.detailsExchange,
          true
        )
        requestToRankingData({
          actionType: "DETAILS_EXCHANGE",
          exchange: foreignRankingData.detailsExchange,
          rankingType: _rankingTypes,
          rankingTypeNum: [detailsTypeNum],
          take: [50],
          sortStatus: sortStatus,
          changeRanking: changeForeignRanking,
          loadResistId: loadResistId,
        })
        store.dispatch(pageLoadRegist(loadResistId, false))
      }
    }

    //ポーリング(1回/15sec)
    useEffect(() => {
      let polling = setInterval(() => {
        dataUpdate()
      }, Config.api.rankingPollingInterval)
      return () => {
        clearInterval(polling)
      }
    }, [foreignRankingData])

    //ページ更新
    useEffect(() => {
      if (isPageLoading) dataUpdate()
    }, [isPageLoading])

    //0の場合は、サマリー・1の場合は、詳細
    return isPhone ? (
      <RankingDetailTemplate
        rankingData={foreignRankingData}
        changeRanking={changeForeignRanking}
        isPhone={isPhone}
        isForeign={true}
      />
    ) : !foreignRankingData.displayType ? (
      <RankingSummaryTemplate
        rankingData={foreignRankingData}
        changeRanking={changeForeignRanking}
        isForeign={true}
      />
    ) : (
      <RankingDetailTemplate
        rankingData={foreignRankingData}
        changeRanking={changeForeignRanking}
        isPhone={isPhone}
        isForeign={true}
      />
    )
  }
)
